import type { AlertRule } from '@owl/shared';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { formatDateTime } from '../../../lib/time';
import { SeverityChip } from '../../shared';
import { BottomSheet } from '../BottomSheet';
import type { MobileShellCtx } from '../MobileShellCtx';

const LABEL: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
  fontWeight: 500,
  marginBottom: 8,
};

export function AlertRuleSheet({ ctx, ruleId }: { ctx: MobileShellCtx; ruleId: string }) {
  const [rule, setRule] = useState<AlertRule | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getAlertRule(ruleId);
        if (!cancelled) setRule(r.rule);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ruleId]);

  return (
    <BottomSheet title="Regla de alerta" onClose={ctx.closeSheet}>
      {err && <div className="m-error-banner">{err}</div>}
      {!rule && !err && <div className="m-loading">Cargando…</div>}
      {rule && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <SeverityChip severity={rule.severity} />
            <span
              className="mono"
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: rule.status === 'active' ? 'var(--ok)' : 'var(--fg-4)',
              }}
            >
              {rule.status}
            </span>
          </div>

          <h3
            style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 500, letterSpacing: '-0.01em' }}
          >
            {rule.name}
          </h3>
          {rule.description && (
            <div
              style={{ color: 'var(--fg-3)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 }}
            >
              {rule.description}
            </div>
          )}

          <div style={LABEL}>Condición</div>
          <div className="rule-cond" style={{ marginBottom: 14 }}>
            {ruleCondition(rule)}
          </div>

          <div style={LABEL}>Configuración</div>
          <div className="kv-block" style={{ marginBottom: 14 }}>
            <div className="kv-row">
              <span className="k">Scope apps</span>
              <span className="v">
                {rule.scopeApplications === 'all'
                  ? 'Todas'
                  : `${rule.scopeApplications.length} apps`}
              </span>
            </div>
            <div className="kv-row">
              <span className="k">Scope tipos</span>
              <span className="v">
                {rule.scopeMessageTypes === 'all'
                  ? 'Todos'
                  : `${rule.scopeMessageTypes.length} tipos`}
              </span>
            </div>
            <div className="kv-row">
              <span className="k">Cooldown</span>
              <span className="v">{rule.cooldownMinutes} min</span>
            </div>
            <div className="kv-row">
              <span className="k">Group similar</span>
              <span className="v">{rule.groupSimilar ? 'sí' : 'no'}</span>
            </div>
            <div className="kv-row">
              <span className="k">Canales</span>
              <span className="v">{summarizeChannels(rule)}</span>
            </div>
          </div>

          <div style={LABEL}>Actividad</div>
          <div className="kv-block">
            <div className="kv-row">
              <span className="k">Disparos 7d</span>
              <span className="v">{rule.triggerCount7d}</span>
            </div>
            <div className="kv-row">
              <span className="k">Último disparo</span>
              <span className="v">
                {rule.lastTriggeredAt ? formatDateTime(rule.lastTriggeredAt) : '—'}
              </span>
            </div>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

function ruleCondition(rule: AlertRule): ReactNode {
  const c = rule.condition;
  switch (c.type) {
    case 'any_match':
      return <>Cualquier evento dentro del scope.</>;
    case 'threshold':
      return (
        <>
          Más de <span className="accent">{c.count}</span> en{' '}
          <span className="accent">{c.windowMinutes}m</span>.
        </>
      );
    case 'content_match':
      return (
        <>
          Match{' '}
          <span className="accent">
            {c.isRegex ? '/' : '"'}
            {c.pattern}
            {c.isRegex ? '/' : '"'}
          </span>{' '}
          en el mensaje.
        </>
      );
    case 'metadata_match':
      return (
        <>
          metadata.<span className="accent">{c.key}</span> {c.operator}{' '}
          <span className="accent">{c.value}</span>
        </>
      );
  }
}

function summarizeChannels(rule: AlertRule): string {
  const ch = rule.channels;
  const parts: string[] = [];
  if (ch.email && ch.email.length > 0) parts.push(`email (${ch.email.length})`);
  if (ch.webhook) parts.push('webhook');
  if (ch.slack) parts.push('slack');
  if (ch.discord) parts.push('discord');
  return parts.length > 0 ? parts.join(' · ') : '—';
}
